import { FormControlMixin } from '@umbraco-ui/uui-base/lib/mixins';
import { defineElement } from '@umbraco-ui/uui-base/lib/registration';
import { demandCustomElement } from '@umbraco-ui/uui-base/lib/utils';
import { UUIComboboxListEvent } from '@umbraco-ui/uui-combobox-list/lib';
import { iconRemove } from '@umbraco-ui/uui-icon-registry-essential/lib/svgs';
import { LitElement, html, css } from 'lit';
import { property, query, queryAssignedElements, state } from 'lit/decorators.js';
import { UUIEvent } from '@umbraco-ui/uui-base/lib/events';

var __defProp$1 = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
class UUIComboboxEvent extends UUIEvent {
  constructor(evName, eventInit = {}) {
    super(evName, __spreadValues(__spreadValues({}, { bubbles: true }), eventInit));
  }
}
UUIComboboxEvent.SEARCH = "search";
UUIComboboxEvent.CHANGE = "change";

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let UUIComboboxElement = class extends FormControlMixin(LitElement) {
  constructor() {
    super(...arguments);
    this.open = false;
    this.closeLabel = "Close";
    this.disabled = false;
    this._displayValue = "";
    this._search = "";
    this._isPhone = false;
    this._onPhoneChange = () => {
      this._isPhone = this.phoneMediaQuery.matches;
    };
    this._onMouseDown = () => requestAnimationFrame(() => this._input.focus());
    this._onBlur = () => requestAnimationFrame(() => {
      var _a;
      if (!((_a = this.shadowRoot) == null ? void 0 : _a.activeElement)) {
        this._onClose();
      }
    });
    this._onInput = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.search = e.target.value;
      this.dispatchEvent(new UUIComboboxEvent(UUIComboboxEvent.SEARCH));
      this._open();
    };
    this._onSlotChange = () => {
      var _a;
      if (this.value && this.value !== ((_a = this._comboboxList) == null ? void 0 : _a.value)) {
        this._updateValue(this.value);
      }
    };
    this._onChange = () => {
      var _a;
      this.value = ((_a = this._comboboxList) == null ? void 0 : _a.value) || "";
      this.search = this.value ? this.search : "";
      this._onClose();
      this.dispatchEvent(new UUIComboboxEvent(UUIComboboxEvent.CHANGE));
    };
    this._open = () => {
      if (this.open)
        return;
      this.open = true;
    };
    this._onClose = () => {
      if (!this.open)
        return;
      this.open = false;
      this.search = "";
      this._input.value = this._displayValue;
      this.dispatchEvent(new UUIComboboxEvent(UUIComboboxEvent.SEARCH));
    };
    this._onKeyDown = (e) => {
      if (this.open === false && e.key === "Enter") {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        this._open();
      }
      if (e.key === "Escape" || e.key === "Enter") {
        this._onClose();
      }
    };
    this._onClear = (e) => {
      if (e.key && e.key !== "Enter")
        return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.value = "";
      this.search = "";
      this._input.value = this._displayValue;
      this._input.focus();
      this.dispatchEvent(new UUIComboboxEvent(UUIComboboxEvent.SEARCH));
      this.dispatchEvent(new UUIComboboxEvent(UUIComboboxEvent.CHANGE));
    };
    this._renderInput = () => {
      return html` <uui-input
      slot="trigger"
      id="combobox-input"
      label="combobox-input"
      type="text"
      .value=${this._displayValue}
      autocomplete="off"
      .disabled=${this.disabled}
      @click=${this._open}
      @input=${this._onInput}
      @keydown=${this._onKeyDown}>
      <slot name="input-prepend" slot="prepend"></slot>
      ${this.disabled ? "" : this._renderClearButton()} ${this._renderCaret()}
      <slot name="input-append" slot="append"></slot>
    </uui-input>`;
    };
    this._renderCaret = () => {
      return html`<svg id="caret" slot="append" viewBox="0 0 512 512">
      <path d="M 255.125 400.35 L 88.193 188.765 H 422.055 Z"></path>
    </svg>`;
    };
    this._renderClearButton = () => {
      return this.value || this.search ? html`<uui-button
          id="clear-button"
          @click=${this._onClear}
          @keydown=${this._onClear}
          label="clear"
          slot="append"
          compact
          style="height: 100%;">
          <uui-icon name="remove" .fallback=${iconRemove.strings[0]}></uui-icon>
        </uui-button>` : "";
    };
    this._renderDropdown = () => {
      return html`<div id="dropdown" slot="popover">
      <uui-scroll-container tabindex="-1" id="scroll-container">
        <slot></slot>
      </uui-scroll-container>
    </div>`;
    };
  }
  get value() {
    return this._value;
  }
  set value(newValue) {
    if (typeof newValue === "string") {
      this._updateValue(newValue);
    }
    super.value = newValue;
  }
  get search() {
    return this._search;
  }
  set search(newValue) {
    if (this.search === newValue)
      return;
    const oldValue = this._search;
    this._search = newValue;
    this.requestUpdate("search", oldValue);
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("blur", this._onBlur);
    this.addEventListener("mousedown", this._onMouseDown);
    this.phoneMediaQuery = window.matchMedia("(max-width: 600px)");
    this._onPhoneChange();
    this.phoneMediaQuery.addEventListener("change", this._onPhoneChange);
    demandCustomElement(this, "uui-icon");
    demandCustomElement(this, "uui-input");
    demandCustomElement(this, "uui-button");
    demandCustomElement(this, "uui-combobox-list");
    demandCustomElement(this, "uui-scroll-container");
    demandCustomElement(this, "uui-popover");
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("blur", this._onBlur);
    this.removeEventListener("mousedown", this._onMouseDown);
    this.phoneMediaQuery.removeEventListener("change", this._onPhoneChange);
  }
  async firstUpdated() {
    var _a;
    const list = (_a = this._comboboxListElements) == null ? void 0 : _a[0];
    if (list) {
      this._comboboxList = list;
      this._comboboxList.for = this;
      this._comboboxList.addEventListener(
        UUIComboboxListEvent.CHANGE,
        this._onChange
      );
      this._comboboxList.addEventListener(
        UUIComboboxListEvent.INNER_SLOT_CHANGE,
        this._onSlotChange
      );
      await this.updateComplete;
      this._updateValue(this.value);
    }
  }
  _updateValue(value) {
    if (this._comboboxList) {
      this._comboboxList.value = value;
      requestAnimationFrame(
        () => this._displayValue = this._comboboxList.displayValue || ""
      );
    }
  }
  getFormElement() {
    return this._input;
  }
  render() {
    if (this._isPhone && this.open) {
      return html` <div id="phone-wrapper">
        <uui-button label="close" look="primary" @click=${this._onClose}>
          ${this.closeLabel}
        </uui-button>
        ${this._renderInput()} ${this._renderDropdown()}
      </div>`;
    } else {
      return html`
        <uui-popover
          .open=${this.open}
          .margin=${-1}
          @close=${() => this._onClose()}>
          ${this._renderInput()} ${this._renderDropdown()}
        </uui-popover>
      `;
    }
  }
};
UUIComboboxElement.styles = [
  css`
      :host {
        display: inline-block;
      }

      #combobox-input {
        width: 100%;
        border-radius: var(--uui-size-1,3px);
      }

      #scroll-container {
        overflow-y: auto;
        width: 100%;
        max-height: var(--uui-combobox-popover-max-height, 500px);
      }

      #dropdown {
        overflow: hidden;
        z-index: -1;
        background-color: var(
          --uui-combobox-popover-background-color,
          var(--uui-color-surface,#fff)
        );
        border: 1px solid var(--uui-color-border,#d8d7d9);
        border-radius: var(--uui-border-radius,3px);
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        box-shadow: var(--uui-shadow-depth-3,0 10px 20px rgba(0,0,0,0.19) , 0 6px 6px rgba(0,0,0,0.23));
      }

      #caret {
        margin-right: var(--uui-size-3, 9px);
        display: flex;
        width: 1.15em;
        flex-shrink: 0;
        margin-top: -1px;
      }

      :host([disabled]) #caret {
        fill: var(--uui-color-disabled-contrast,#c4c4c4);
      }

      #phone-wrapper {
        position: fixed;
        inset: 0;
        display: flex;
        flex-direction: column;
        z-index: 1;
        font-size: 1.1em;
      }

      #phone-wrapper #dropdown {
        display: flex;
      }

      #phone-wrapper #combobox-input {
        height: var(--uui-size-16,48px);
      }

      #phone-wrapper > uui-button {
        height: var(--uui-size-14,42px);
        width: 100%;
      }

      #phone-wrapper #scroll-container {
        max-height: unset;
      }
    `
];
__decorateClass([
  property({ attribute: "value", reflect: true })
], UUIComboboxElement.prototype, "value", 1);
__decorateClass([
  property({ type: String })
], UUIComboboxElement.prototype, "search", 1);
__decorateClass([
  property({ type: Boolean })
], UUIComboboxElement.prototype, "open", 2);
__decorateClass([
  property({ type: String })
], UUIComboboxElement.prototype, "closeLabel", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], UUIComboboxElement.prototype, "disabled", 2);
__decorateClass([
  query("#combobox-input")
], UUIComboboxElement.prototype, "_input", 2);
__decorateClass([
  queryAssignedElements({
    flatten: true,
    selector: "uui-combobox-list"
  })
], UUIComboboxElement.prototype, "_comboboxListElements", 2);
__decorateClass([
  state()
], UUIComboboxElement.prototype, "_displayValue", 2);
__decorateClass([
  state()
], UUIComboboxElement.prototype, "_search", 2);
__decorateClass([
  state()
], UUIComboboxElement.prototype, "_isPhone", 2);
UUIComboboxElement = __decorateClass([
  defineElement("uui-combobox")
], UUIComboboxElement);

export { UUIComboboxElement, UUIComboboxEvent };
